// @ts-nocheck
import React from 'react';
import { nanoid } from 'nanoid';
import _ from 'lodash';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';
import ReactPlayer from 'react-player';
import { ExclamationIcon, PencilIcon, PlayIcon, UploadIcon } from '@heroicons/react/solid';
import {
  Card,
  Checkbox,
  CheckboxChangeEvent,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Switch,
  Upload,
} from 'antd';

import { ErrorFallback } from '../../common/Error/Fallback';
import Loading from '../../common/Loading';
import RTEditor from '../../common/RTEditor';
import TextCustomization from './TextCustomization';
import ContractTypeLookup from '../../common/Search/ContractTypeLookup';
import CountryLookUp from '../../common/Search/CountryLookup';
import CourseCategoryLookup from '../../common/Search/CourseCategory';
import CourseLevelLookup from '../../common/Search/CourseLevel';
import LetterCategoryLookup from '../../common/Search/LetterCategoryLookup';
import { errorMessages } from '../../helpers/messages';
import { delay, getBase64, isNullUndefined, mustBeArray } from '../../helpers/util';
import { DocumentType } from '../../@types/letter';

interface IPublishSettingProps {
  content: any;
  config: Array<any>;
  updateSettings: Function;
  actionHelpers?: Function;
  updateContentType?: Function;
  reloadContent?: Function;
  initialValue?: any;
  status?: Array<any>;
  id: any;
  visible: boolean;
}

interface IPublishSettingState {
  imageLoading: boolean;
  videoLoading: boolean;
  id: string;
  contractTypeID?: string;
  categoryID?: string;
  employmentTypes?: string[];
  isWorkerSectionActive?: boolean;
  isManagerSectionActive?: boolean;
  workerDocumentAcceptanceOption?: string;
  managerDocumentAcceptanceOption?: string;
  requireEmployeeAcknowledgement?: boolean;
  requireEmployeeSignature?: boolean;
  requireApproval?: boolean;
  requireSignature?: boolean;
}

const CheckboxInput = (props) => props.render();

const getPartialLabel = (
  label: string,
  inputPosition: number,
  location: 'BeforeInput' | 'AfterInput',
): string => {
  const wordArr = label.split(' ');
  const filteredWordArr =
    location === 'BeforeInput'
      ? wordArr.filter((word, index) => index < inputPosition - 1)
      : wordArr.filter((word, index) => index >= inputPosition - 1);
  return filteredWordArr.join(' ');
};

export const StyledSectionTitle = styled.span`
  font-size: 16px;
  font-weight: bold;
`;

// Styled component for sub-checkboxes/radios to indent them
const StyledNestedOptionContainer = styled.div`
  margin-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

class Setting extends React.Component<IPublishSettingProps, IPublishSettingState> {
  state: IPublishSettingState = {
    imageLoading: false,
    videoLoading: false,
    id: nanoid(),
    employmentTypes: [],
    isWorkerSectionActive: false,
    isManagerSectionActive: false,
    workerDocumentAcceptanceOption: '',
    managerDocumentAcceptanceOption: '',
    requireEmployeeAcknowledgement: false,
    requireEmployeeSignature: false,
    requireApproval: false,
    requireSignature: false,
  };

  private getDefaultValue = (type: string) => {
    switch (type) {
      case 'textCustomization':
        return {
          fontName: '',
          fontSize: '16px',
          isBold: false,
          isItalic: false,
          isUnderline: false,
          fontColor: '#000000',
          underlineHeader: false,
          IsLevelClauseNumbering: false,
        };
      case 'multiselect':
      case 'checkboxGroup':
        return [];
      case 'switch':
      case 'parentCheckbox':
        return false;
      default:
        return '';
    }
  };

  componentDidMount() {
    this.initializeFields();
  }

  initializeFields = () => {
    const { content, config, updateSettings } = this.props;

    delay(500).then(() => {
      this.setState(
        (prevState) => {
          const newState = mustBeArray(config).reduce((acc, c) => {
            let fieldValue;
            if (c.type === 'textCustomization') {
              const isAllNull = content[c.key]
                ? Object.values(content[c.key]).every((val) => val === null || val === undefined)
                : true;

              fieldValue =
                isAllNull || isNullUndefined(content[c.key])
                  ? this.getDefaultValue(c.type)
                  : content[c.key];
            } else {
              fieldValue = isNullUndefined(content[c.key])
                ? this.getDefaultValue(c.type)
                : content[c.key];
            }

            return {
              ...acc,
              [c.key]: fieldValue,
            };
          }, prevState);

          if (_.isEmpty(newState.employmentTypes) && newState.type === DocumentType.Policy) {
            newState.employmentTypes = ['FullTime', 'PartTime', 'Casual', 'Independent'];
          }

          newState.isWorkerSectionActive =
            content.requireEmployeeAcknowledgement || content.requireEmployeeSignature || false;
          newState.isManagerSectionActive =
            content.requireApproval || content.requireSignature || false;

          if (content.requireEmployeeAcknowledgement) {
            newState.workerDocumentAcceptanceOption = 'workerAcknowledgement';
          } else if (content.requireEmployeeSignature) {
            newState.workerDocumentAcceptanceOption = 'workerSignature';
          } else {
            newState.workerDocumentAcceptanceOption = '';
          }

          if (content.requireApproval) {
            newState.managerDocumentAcceptanceOption = 'managerAcknowledgement';
          } else if (content.requireSignature) {
            newState.managerDocumentAcceptanceOption = 'managerSignature';
          } else {
            newState.managerDocumentAcceptanceOption = '';
          }

          newState.requireEmployeeAcknowledgement =
            content.requireEmployeeAcknowledgement || content.requireEmployeeSignature || false;
          newState.requireEmployeeSignature = content.requireEmployeeSignature || false;

          newState.requireApproval = content.requireApproval || content.requireSignature || false;
          newState.requireSignature = content.requireSignature || false;

          return {
            ...newState,
            isAdditionalAttemptsEnabled: !!newState.additionalAttempts,
            isCompletionDaysEnabled: !!newState.completionDays,
          };
        },
        () => {
          updateSettings(this.state);
        },
      );
    });
  };

  componentDidUpdate(prevProps: IPublishSettingProps) {
    if (prevProps.visible !== this.props.visible) {
      this.initializeFields();
    }
  }

  beforeUpload = (file: any, elem: any) => {
    const { id } = this.props;
    const isValidType =
      elem.type === 'image'
        ? ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)
        : ['video/mp4', 'video/mov', 'video/mpeg'].includes(file.type);
    if (!isValidType) {
      return message.error(errorMessages.invalidFileType);
    }
    this.setState((prevState) => {
      prevState = {
        ...prevState,
        [`${elem.type}Loading`]: true,
      };
      return prevState;
    });
    this.props
      .actionHelpers({
        payLoad: { file, name: file.name },
        options: { id, type: elem.type, content: elem.key },
      })
      .then((resp: any) => {
        this.setState((prevState) => {
          prevState = {
            ...prevState,
            [`${elem.type}Loading`]: false,
          };
          return prevState;
        });
        if (resp.status) {
          if (elem.type === 'image') {
            getBase64(file, (imageUrl: string) => {
              this.setState({
                ...this.state,
                [elem.key]: imageUrl,
              });
            });
          } else {
            const rPlayer = document.querySelector('.react-player').getElementsByTagName('video');
            if (_.isUndefined(rPlayer[0])) {
              this.props.reloadContent({ options: { id } });
            } else {
              rPlayer[0].src = URL.createObjectURL(file);
            }
          }
          return message.success({ content: `${elem.type} successfully uploaded` });
        } else {
          return message.error('File Format not supported');
        }
      });
    return false;
  };

  handleChange = (key: string, value: any, elem: any) => {
    this.setState(
      (prevState) => {
        const newState = {
          ...prevState,
          [key]: value,
          ...(elem?.inverse && { [elem.inverse]: value ? false : '' }),
        };

        if (_.isEmpty(newState.employmentTypes) && newState.type === DocumentType.Policy) {
          newState.employmentTypes = ['FullTime', 'PartTime', 'Casual', 'Independent'];
        }

        return newState;
      },
      () => {
        this.props.updateSettings(this.state);
      },
    );
  };

  handleCheckboxGroupChange = (key: string, checkedValues: string[]) => {
    this.setState(
      {
        ...this.state,
        [key]: checkedValues,
      } as Pick<IPublishSettingState, keyof IPublishSettingState>,
      () => {
        this.props.updateSettings(this.state);
      },
    );
  };

  handleParentCheckboxChange = (
    parentKey: string,
    nestedRadioKey: string,
    childrenKeys: string[],
    checked: boolean,
  ) => {
    this.setState(
      (prevState) => {
        const newState = {
          ...prevState,
          [parentKey]: checked,
        };
        if (!checked) {
          childrenKeys.forEach((childKey) => {
            newState[childKey] = false;
          });
          newState[nestedRadioKey] = '';
        } else {
          if (!newState[nestedRadioKey] && childrenKeys.length > 0) {
            if (nestedRadioKey === 'workerDocumentAcceptanceOption') {
              newState['workerDocumentAcceptanceOption'] = 'workerAcknowledgement';
              newState['requireEmployeeAcknowledgement'] = true;
              newState['requireEmployeeSignature'] = false;
            } else if (nestedRadioKey === 'managerDocumentAcceptanceOption') {
              newState['managerDocumentAcceptanceOption'] = 'managerAcknowledgement';
              newState['requireApproval'] = true;
              newState['requireSignature'] = false;
            }
          }
        }
        return newState;
      },
      () => {
        this.props.updateSettings(this.state);
      },
    );
  };

  handleNestedRadioChange = (
    parentSectionKey: string,
    radioGroupKey: string,
    e: Radio.RadioChangeEvent,
    childrenBooleanKeys: { value: string; key: string }[],
  ) => {
    const selectedValue = e.target.value;
    this.setState(
      (prevState) => {
        const newState = {
          ...prevState,
          [radioGroupKey]: selectedValue,
        };

        childrenBooleanKeys.forEach((child) => {
          newState[child.key] = false;
        });

        const selectedChild = childrenBooleanKeys.find((child) => child.value === selectedValue);
        if (selectedChild) {
          newState[selectedChild.key] = true;
        }

        if (!newState[parentSectionKey] && selectedValue) {
          if (parentSectionKey === 'isWorkerSectionActive') {
            newState.isWorkerSectionActive = true;
          } else if (parentSectionKey === 'isManagerSectionActive') {
            newState.isManagerSectionActive = true;
          }
        }

        if (newState.isWorkerSectionActive) {
          newState.requireEmployeeAcknowledgement =
            newState.requireEmployeeAcknowledgement || newState.requireEmployeeSignature;
        }

        if (newState.isManagerSectionActive) {
          newState.requireApproval = newState.requireApproval || newState.requireSignature;
        }

        return newState;
      },
      () => {
        this.props.updateSettings(this.state);
      },
    );
  };

  getEnableLabel = (label: string) => `is${label.charAt(0).toUpperCase() + label.slice(1)}Enabled`;

  handleCheckboxInputEnable = (e: CheckboxChangeEvent, elem: unknown): void => {
    !e.target.checked
      ? this.setState(
          {
            ...this.state,
            [elem.key]: elem.key === 'completionDays' ? null : elem.min,
            [this.getEnableLabel(elem.key)]: false,
          },
          () => {
            this.props.updateSettings(this.state, elem);
          },
        )
      : this.setState(
          {
            ...this.state,
            [this.getEnableLabel(elem.key)]: true,
            [elem.key]: elem.initialValue,
          },
          () => {
            this.props.updateSettings(this.state, elem);
          },
        );
  };

  mapPropsToValues = (propsArray: Array<{ [key: string]: string }>, state: any) => {
    return propsArray.reduce((acc, prop) => {
      const [[key, stateKey]] = Object.entries(prop);
      return {
        ...acc,
        [key]: state[stateKey],
      };
    }, {});
  };

  render() {
    const { config, content } = this.props;

    return (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <div className='grid grid-cols-6 gap-4'>
          {mustBeArray(config).map((elem, i) => {
            switch (elem.type) {
              case 'textbox':
                return (
                  <div key={i} className={'col-span-3'}>
                    {this.state[`${elem.key}-editable`] && (
                      <div className={'flex items-center gap-6 text-red-400'}>
                        <ExclamationIcon className='h-8 w-8 m-r-2' />
                        <span>
                          Changing the title may confuse clients already using this contract
                          template
                        </span>
                      </div>
                    )}
                    <label htmlFor='fld-course-name' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    {elem.subLabel && (
                      <label htmlFor='fld-course-name' className='m-b-12 f-w-400'>
                        {elem.subLabel}
                      </label>
                    )}
                    <Input
                      value={this.state[elem.key]}
                      placeholder={elem.placeholder}
                      disabled={elem.confirmEdit && !this.state[`${elem.key}-editable`]}
                      onChange={(value) =>
                        this.handleChange(elem.key, value.currentTarget.value, elem)
                      }
                      addonAfter={
                        elem.confirmEdit && !this.state[`${elem.key}-editable`] ? (
                          <PencilIcon
                            className='h-5 w-5'
                            onClick={() => this.handleChange(`${elem.key}-editable`, true, elem)}
                          />
                        ) : (
                          <div />
                        )
                      }
                    />
                  </div>
                );

              case 'simple-textarea':
                return (
                  <div key={i} className={'col-span-6'}>
                    {this.state[`${elem.key}-editable`] && (
                      <div className={'flex items-center text-red-400'}>
                        <ExclamationIcon className='h-8 w-8' />
                        <span>
                          Changing the title may confuse clients already using this contract
                          template
                        </span>
                      </div>
                    )}
                    <label htmlFor='fld-course-name' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    {elem.subLabel && (
                      <label htmlFor='fld-course-name' className='m-b-12 f-w-400'>
                        {elem.subLabel}
                      </label>
                    )}
                    <Input.TextArea
                      value={this.state[elem.key]}
                      placeholder={elem.placeholder}
                      disabled={elem.confirmEdit && !this.state[`${elem.key}-editable`]}
                      onChange={(value) =>
                        this.handleChange(elem.key, value.currentTarget.value, elem)
                      }
                      addonAfter={
                        elem.confirmEdit &&
                        !this.state[`${elem.key}-editable`] && (
                          <PencilIcon
                            className='h-5 w-5'
                            onClick={() => this.handleChange(`${elem.key}-editable`, true, elem)}
                          />
                        )
                      }
                    />
                  </div>
                );

              case 'textarea':
                return (
                  elem.display && (
                    <div key={i} className={'col-span-6'}>
                      <div className='mb-4'>
                        <label htmlFor='fld-course-description' className='block mb-2 f-w-600'>
                          {elem.label}
                        </label>
                        {elem.subLabel && (
                          <label htmlFor='fld-course-name' className='block mb-2 f-w-400'>
                            {elem.subLabel}
                          </label>
                        )}
                      </div>
                      <RTEditor
                        value={this.state[elem.key]}
                        visible={true}
                        handleEditorChange={(value) => this.handleChange(elem.key, value, elem)}
                      />
                    </div>
                  )
                );

              case 'switch':
                return (
                  elem.display && (
                    <div key={i} className={'col-span-3'}>
                      <Switch
                        checked={this.state[elem.key] || false}
                        onChange={(checked) => this.handleChange(elem.key, checked, elem)}
                      />{' '}
                      {elem.label}
                    </div>
                  )
                );
              case 'contractTypeDD':
                return (
                  <div key={i} className={'col-span-3'}>
                    <label htmlFor='fld-contract-type' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    <ContractTypeLookup
                      disabled={elem.disabled}
                      value={this.state[elem.key]}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                    />
                  </div>
                );
              case 'letterCategoryID':
                return (
                  <div key={i} className={'col-span-3'}>
                    <label htmlFor='fld-letter-category' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    <LetterCategoryLookup
                      disabled={elem.disabled}
                      value={this.state[elem.key]}
                      {...this.mapPropsToValues(elem.props, this.state)}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                    />
                  </div>
                );
              case 'courseCategoryDD':
                return (
                  <div key={i} className={'col-span-3'}>
                    <label htmlFor='fld-course-category' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    <CourseCategoryLookup
                      value={this.state[elem.key]}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                    />
                  </div>
                );
              case 'courseLevelDD':
                return (
                  <div key={i} className={'col-span-3'}>
                    <label htmlFor='fld-course-level' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    <CourseLevelLookup
                      value={this.state[elem.key]}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                    />
                  </div>
                );
              case 'select':
                return (
                  <div key={i} className={'col-span-3'}>
                    <label htmlFor='fld-course-category' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    <Select
                      showSearch
                      style={{ width: '100%' }}
                      disabled={elem.disabled}
                      value={this.state[elem.key]}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                    >
                      {elem.placeholder && (
                        <Select.Option key='dummy' value={0}>
                          {elem.placeholder}
                        </Select.Option>
                      )}

                      {this.props[elem.data] &&
                        mustBeArray(this.props[elem.data]).map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      {elem.options &&
                        mustBeArray(elem.options).map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </div>
                );
              case 'countryDD':
                return (
                  <div key={i} className={'col-span-3'}>
                    <label htmlFor='fld-country' className='m-b-4 f-w-600'>
                      {elem.label}
                    </label>
                    <CountryLookUp
                      value={this.state[elem.key]}
                      disabled={elem.disabled}
                      size={'default'}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                      width={'100%'}
                      leftMargin={'0'}
                    />
                  </div>
                );
              case 'multiselect':
                return (
                  <div key={i} className={'col-span-3 multiselect-country'}>
                    <label htmlFor='fld-country' className='m-b-4'>
                      {elem.label}
                    </label>
                    <CountryLookUp
                      value={this.state[elem.key]}
                      mode='multiple'
                      disabled={elem.disabled}
                      size={'default'}
                      onChange={(value) => this.handleChange(elem.key, value, elem)}
                      width={'100%'}
                      leftMargin={'0'}
                    />
                  </div>
                );
              case 'image':
                return (
                  <div key={i} className={'col-span-6 mt-2'}>
                    <p className='m-b-4'>{elem.label}</p>
                    <Card className='bg-gray-400 radius-2  flex items-center justify-center '>
                      <Upload
                        className='hand'
                        accept='.jpg,.jpeg,.png'
                        beforeUpload={(file) => this.beforeUpload(file, elem)}
                        showUploadList={false}
                      >
                        <div
                          className={`bg-auto bg-no-repeat bg-center flex items-center justify-center ${
                            this.state[elem.key] ? 'ant-uploaded-file' : 'ant-uploaded-none'
                          }`}
                          style={{
                            height: 350,
                            width: 500,
                            backgroundImage: `url(${this.state[elem.key]})`,
                          }}
                        >
                          {this.state.imageLoading ? (
                            <Loading />
                          ) : (
                            <div className='ant-upload-icon' style={{ position: 'absolute' }}>
                              <UploadIcon className='h-5 w-5' />
                              <span className={'-ml-3'} style={{ display: 'block' }}>
                                Browse
                              </span>
                            </div>
                          )}
                        </div>
                      </Upload>
                    </Card>
                  </div>
                );

              case 'video':
                return (
                  <div key={i} className={'col-span-6'}>
                    <p className='m-b-4'>{elem.label}</p>
                    <Card className='bg-gray-400  flex items-center justify-center  radius-2'>
                      <Upload
                        className='hand'
                        accept='.mp4,.mpeg,.mov'
                        beforeUpload={(file) => this.beforeUpload(file, elem)}
                        showUploadList={false}
                      >
                        <div
                          style={{ height: 350 }}
                          className={`flex items-center justify-center ${
                            content[elem.key] ? 'ant-uploaded-file' : 'ant-uploaded-none'
                          }`}
                        >
                          <ReactPlayer
                            url={content[elem.key]}
                            className='react-player'
                            height='100%'
                            width='auto'
                            controls={true}
                          />
                          {this.state.videoLoading ? (
                            <Loading />
                          ) : (
                            <div className='ant-upload-icon' style={{ position: 'absolute' }}>
                              <PlayIcon className='h-5 w-5' />
                              <span className={'-ml-3'} style={{ display: 'block' }}>
                                Browse
                              </span>
                            </div>
                          )}
                        </div>
                      </Upload>
                    </Card>
                  </div>
                );
              case 'checkboxInput':
                return (
                  <div key={i} className={'col-span-6'}>
                    <CheckboxInput
                      render={() => (
                        <Checkbox
                          checked={this.state[this.getEnableLabel(elem.key)]}
                          onChange={(e: CheckboxChangeEvent) =>
                            this.handleCheckboxInputEnable(e, elem)
                          }
                        >
                          {getPartialLabel(elem.label, elem.inputPosition, 'BeforeInput')}
                          <InputNumber
                            className='mx-2'
                            min={elem.min}
                            max={elem.max}
                            controls={false}
                            value={this.state[elem.key]}
                            placeholder={elem.placeholder}
                            disabled={!this.state[this.getEnableLabel(elem.key)]}
                            onChange={(value) => {
                              this.handleChange(elem.key, value, elem);
                            }}
                          />
                          {getPartialLabel(elem.label, elem.inputPosition, 'AfterInput')}
                        </Checkbox>
                      )}
                    />
                  </div>
                );
              case 'sectionTitle':
                return (
                  <div key={i} className={'col-span-6'}>
                    <StyledSectionTitle>
                      <h4>{elem.label}</h4>
                    </StyledSectionTitle>
                  </div>
                );
              case 'textCustomization':
                return (
                  <div key={i} className={'col-span-6'}>
                    <TextCustomization
                      settings={this.state[elem.key]}
                      onSettingsChange={(textConfig) =>
                        this.handleChange(elem.key, textConfig, elem)
                      }
                    />
                  </div>
                );
              case 'checkboxGroup':
                return (
                  elem.display && (
                    <div key={i} className={'col-span-6'}>
                      <div className='mb-2 f-w-500'>{elem.label}</div>
                      <div className={'flex flex-col gap-2'}>
                        <Checkbox.Group
                          options={elem.options}
                          value={this.state[elem.key]}
                          onChange={(checkedValues: string[]) =>
                            this.handleCheckboxGroupChange(elem.key, checkedValues)
                          }
                          className='flex flex-col'
                        />
                      </div>
                    </div>
                  )
                );
              case 'parentCheckboxGroup':
                return (
                  <div key={i} className={'col-span-6'}>
                    <div className={'mb-4'}>
                      <div className='mb-1 f-w-600'>{elem.label}</div>
                      {elem.guide && <div className='mb-4 f-w-600 text-gray-700'>{elem.guide}</div>}
                    </div>

                    <div className='flex flex-col'>
                      {elem.sections.map((section, sectionIdx) => (
                        <div key={sectionIdx}>
                          <Checkbox
                            checked={this.state[section.parentKey]}
                            className={'mb-4'}
                            onChange={(e) =>
                              this.handleParentCheckboxChange(
                                section.parentKey,
                                section.nestedRadioKey,
                                section.children.map((child) => child.key), // Pass keys for clearing
                                e.target.checked,
                              )
                            }
                          >
                            {section.label}
                          </Checkbox>
                          {this.state[section.parentKey] && ( // Conditionally render children if parent is checked
                            <StyledNestedOptionContainer>
                              {section.type === 'radioGroup' && (
                                <Radio.Group
                                  className='flex flex-col gap-2'
                                  onChange={(e) =>
                                    this.handleNestedRadioChange(
                                      section.parentKey,
                                      section.nestedRadioKey,
                                      e,
                                      section.children, // Pass the children config
                                    )
                                  }
                                  value={this.state[section.nestedRadioKey]}
                                >
                                  {section.children.map((child, childIdx) => (
                                    <Radio key={childIdx} value={child.value}>
                                      {child.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              )}
                            </StyledNestedOptionContainer>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              default:
                return <div key={i}>Default</div>;
            }
          })}
        </div>
      </ErrorBoundary>
    );
  }
}

export default Setting;
